// react
import { ChangeEvent, MouseEvent, useState } from 'react';

// types
import {
	ActionStatus,
	Benefit,
	Contract,
	Document,
	Entity,
	ExpenseType,
	Feedback,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Chip,
	Grid,
	Link,
	Paper,
	Radio,
	SxProps,
	Typography,
} from '@mui/material';

// types
import { Transaction } from '../../../@types';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// ui
import { ChipTraceability } from '../../ui/ChipTraceability/ChipTraceability';
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { IconList, IconListProps } from '../../ui/IconList/IconList';
import { MetadataField } from '../../ui/MetadataField/MetadataField';

// fields
import { Input } from '../../fields/Input/Input';

// links
import { LinkContract } from '../../links/LinkContract/LinkContract';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dates';
import { downloadFile, getPathFromUrl } from '../../../utils/documents';
import { translateTransactionType } from '../../../utils/translations';

// icons
import {
	CalendarTodayOutlined as IconCalendarToday,
	PersonOutlined as IconPerson,
} from '@mui/icons-material';

// theme
import { theme } from '../../../theme';

// props
export type ConfirmationSelection = 'NO' | 'PARTIAL' | 'YES';

export type CardTransactionProps = {
	hasReceiptConfirmation?: boolean;
	onClick: (e: MouseEvent<HTMLButtonElement>) => void;
	onConfirmationSuccess?: () => void;
	transaction: Transaction;
};

export const CardTransaction = ({
	hasReceiptConfirmation,
	onClick,
	onConfirmationSuccess,
	transaction,
}: CardTransactionProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [amountReceived, setAmountReceived] = useState<string>(String(transaction.amount || ''));
	const [confirmationSelected, setConfirmationSelected] = useState<ConfirmationSelection | null>(
		null
	);
	const [documents, setDocuments] = useState<Document[]>([]);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [isExpanded, setIsExpanded] = useState(false);
	const [statusConfirmation, setStatusConfirmation] = useState<ActionStatus>('DEFAULT');

	const { fetchRequest: fetchDocuments, isLoading: isLoadingDocuments } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/api/proxies/documents?transaction=${transaction.id}&populate=type,user`,
		options: {
			headers: {
				Authorization: `Bearer ${sessionStorage.getItem('token')}`,
				apikey: process.env.REACT_APP_APIKEY || '',
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocuments(res.data);
			}
		},
	});

	const { fetchRequest: patchTransaction } = useFetch({
		isLazy: true,
		onSuccess: () => {
			setFeedback(null);
			setStatusConfirmation('SUCCESS');
			if (onConfirmationSuccess) {
				onConfirmationSuccess();
			}
		},
		onError: () => {
			const feedbackNew: Feedback = {
				alert: {
					children: t('error.default'),
					severity: 'error',
				},
			};
			setFeedback(feedbackNew);
			setStatusConfirmation('ERROR');
		},
	});

	const sendPatchTransaction = ({ amount }: { amount: null | number | string }) => {
		setFeedback(null);

		let amountReceived = amount;
		let isReceived = false;

		if (amountReceived) {
			amountReceived =
				typeof amountReceived === 'string' ? parseFloat(amountReceived) : amountReceived;
			isReceived = true;

			if (isNaN(amountReceived)) {
				const feedbackNew: Feedback = {
					alert: {
						children: t('error.invalidAmount'),
						severity: 'error',
					},
				};
				setFeedback(feedbackNew);
				return;
			}
		}

		setStatusConfirmation('LOADING');
		patchTransaction(
			`${process.env.REACT_APP_API_URL}/api/proxies/transactions/${transaction.id}`,
			{
				method: 'PATCH',
				headers: {
					Authorization: `Bearer ${sessionStorage.getItem('token')}`,
					'Content-Type': 'application/json',
					apikey: process.env.REACT_APP_APIKEY || '',
				},
				body: JSON.stringify({ amountReceived, isReceived }),
			}
		);
	};

	// handlers
	const handleClickToggle = async () => {
		if (!isExpanded) {
			fetchDocuments();
		}

		setIsExpanded(!isExpanded);
	};

	const handleClickNo = () => {
		setConfirmationSelected('NO');
		sendPatchTransaction({ amount: null });
	};

	const handleClickPartial = () => {
		setConfirmationSelected('PARTIAL');
	};

	const handleClickYes = () => {
		setConfirmationSelected('YES');
		sendPatchTransaction({ amount: transaction.amount });
	};

	const handleClickConfirmReceipt = () => {
		sendPatchTransaction({ amount: amountReceived });
	};

	const handleChangeAmount = (e: ChangeEvent<HTMLInputElement>) => {
		setAmountReceived(e.target.value);
	};

	const handleClickRestart = () => {
		setAmountReceived(String(transaction.amount || ''));
		setFeedback(null);
		setStatusConfirmation('EDITING');
		setConfirmationSelected(null);
	};

	const renderReceived = () => {
		if (transaction.amount && transaction.currency) {
			return formatCurrency({
				currency: transaction.currency,
				value: transaction.amount,
			});
		}

		if (transaction.quantity && transaction.benefit) {
			return `${transaction.quantity} ${(transaction.benefit as Benefit).name}`;
		}

		return t('na');
	};

	const list: IconListProps['list'] = [];

	if (transaction.fromEntity) {
		list.push({
			IconCircleProps: {
				type: 'entity',
			},
			value: <Typography>{(transaction.fromEntity as Entity)?.name}</Typography>,
		});
	}

	if (transaction.contract) {
		list.push({
			IconCircleProps: {
				type: 'contract',
			},
			value: <LinkContract contract={transaction.contract as Contract} />,
		});
	}

	let btnExpandLabel = t('seeDocuments');

	if (isExpanded) {
		btnExpandLabel = t('hideDocuments');
	}

	let contentExpanded = <></>;

	if (documents.length) {
		contentExpanded = (
			<>
				{documents?.map((document, index) => {
					const path = getPathFromUrl(document.url);

					return (
						<Box
							key={`document=${index}`}
							sx={(theme) => {
								let sx: SxProps = {
									padding: theme.spacing(2),
								};

								if (index !== 0) {
									sx = {
										...sx,
										borderTop: `1px solid ${theme.palette.divider}`,
									};
								}

								return sx;
							}}>
							<Grid item xs={12}>
								<Grid container spacing={1}>
									<Grid item xs={12}>
										<Grid alignItems="center" container spacing={1}>
											<Grid item>
												<Grid container spacing={1}>
													<Grid item xs={12}>
														<Link
															component="button"
															onClick={(e) =>
																downloadFile(e, path, document.metadata.type, document.name)
															}>
															{document.name}
														</Link>
														{document.type && <Typography>{document.type.name}</Typography>}
													</Grid>
													<Grid item xs={12}>
														<Grid container spacing={2}>
															<Grid item>
																<MetadataField
																	icon={<IconCalendarToday />}
																	label={formatDate({ date: new Date(document.created) })}
																/>
															</Grid>

															{document.user && (
																<Grid item>
																	<MetadataField
																		icon={<IconPerson />}
																		label={document.user.fullName}
																	/>
																</Grid>
															)}
														</Grid>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Box>
					);
				})}
			</>
		);
	} else {
		contentExpanded = (
			<Alert
				severity="info"
				sx={(theme) => {
					return {
						borderRadius: 0,
					};
				}}>
				{isLoadingDocuments ? `${t('loading')}...` : t('noDocuments')}
			</Alert>
		);
	}

	let contentReceiptWrapper = null;
	let contentReceipt = null;

	if (hasReceiptConfirmation) {
		if (
			typeof transaction.isReceived === 'boolean' &&
			statusConfirmation !== 'EDITING' &&
			statusConfirmation !== 'LOADING'
		) {
			if (transaction.isReceived) {
				// transaction is marked as received
				contentReceipt = (
					<Grid item xs={12}>
						<Alert
							action={
								<Button color="inherit" onClick={handleClickRestart}>
									{t('update')}
								</Button>
							}
							severity="success"
							sx={{ alignItems: 'center', '& .MuiAlert-action': { marginRight: 0 } }}
							variant="outlined">
							{t('received')}
						</Alert>
					</Grid>
				);
			} else {
				// transaction is marked as not received
				contentReceipt = (
					<Grid item xs={12}>
						<Alert
							action={
								<Button color="inherit" onClick={handleClickRestart}>
									{t('update')}
								</Button>
							}
							severity="error"
							sx={{ alignItems: 'center', '& .MuiAlert-action': { marginRight: 0 } }}
							variant="outlined">
							{t('notReceived')}
						</Alert>
					</Grid>
				);
			}
		} else if (statusConfirmation === 'SUCCESS') {
			// receipt confirmation successful
			contentReceipt = (
				<Grid item xs={12}>
					<Alert severity="success">{t('receiptConfirmationSuccess')}</Alert>
				</Grid>
			);
		} else if (statusConfirmation === 'ERROR') {
			// receipt confirmation error
			contentReceipt = (
				<Grid item xs={12}>
					<Alert severity="error">{t('error.default')}</Alert>
				</Grid>
			);
		} else {
			// ask user to confirm receipt
			contentReceipt = (
				<Grid item xs={12}>
					<Box
						sx={(theme) => ({
							backgroundColor: theme.palette.brand.grey[100],
							borderColor: theme.palette.divider,
							borderRadius: 1,
							borderStyle: 'solid',
							borderWidth: 1,
							padding: theme.spacing(2),
						})}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Grid container spacing={1}>
									<Grid item xs={12}>
										<Typography fontWeight={600}>{t('received')}</Typography>
									</Grid>
									<Grid item xs={12}>
										<Grid container spacing={2}>
											<Grid item xs={4}>
												<Button
													color="error"
													disabled={statusConfirmation === 'LOADING'}
													fullWidth
													onClick={handleClickNo}
													size="small"
													sx={{ justifyContent: 'flex-start' }}
													variant="outlined">
													<Radio
														color="error"
														checked={confirmationSelected === 'NO'}
														disabled={statusConfirmation === 'LOADING'}
														disableRipple={true}
														size="small"
														sx={{ padding: theme.spacing(0, 1, 0, 0) }}
													/>
													{t('no')}
												</Button>
											</Grid>
											<Grid item xs={4}>
												<Button
													color="neutral"
													disabled={statusConfirmation === 'LOADING'}
													fullWidth
													onClick={handleClickPartial}
													size="small"
													sx={{ justifyContent: 'flex-start' }}
													variant="outlined">
													<Radio
														color="primary"
														checked={confirmationSelected === 'PARTIAL'}
														disableRipple={true}
														disabled={statusConfirmation === 'LOADING'}
														size="small"
														sx={{ padding: theme.spacing(0, 1, 0, 0) }}
													/>
													{t('partial')}
												</Button>
											</Grid>
											<Grid item xs={4}>
												<Button
													color="success"
													disabled={statusConfirmation === 'LOADING'}
													fullWidth
													onClick={handleClickYes}
													size="small"
													sx={{ justifyContent: 'flex-start' }}
													variant="outlined">
													<Radio
														color="primary"
														checked={confirmationSelected === 'YES'}
														disableRipple={true}
														disabled={statusConfirmation === 'LOADING'}
														size="small"
														sx={{ padding: theme.spacing(0, 1, 0, 0) }}
													/>
													{t('yes')}
												</Button>
											</Grid>
											{confirmationSelected && statusConfirmation === 'LOADING' && (
												<Grid item xs={12}>
													<Typography color="textSecondary" variant="caption">
														{t('loading')}...
													</Typography>
												</Grid>
											)}
										</Grid>
									</Grid>
								</Grid>
							</Grid>

							{/* show amount input if partial */}
							{confirmationSelected === 'PARTIAL' && (
								<Grid item xs={12}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<Input
												currency={transaction.currency}
												fullWidth
												inputProps={{
													max: transaction.amount,
													min: 0,
													step: 0.01,
												}}
												label={t('amountReceived')}
												onChange={handleChangeAmount}
												type="number"
												value={amountReceived}
											/>
										</Grid>
										{feedback && (
											<Grid item xs={12}>
												<Alert {...feedback.alert}>
													{feedback.title && <AlertTitle {...feedback.title} />}
													{feedback.alert.children}
												</Alert>
											</Grid>
										)}
										<Grid item xs={12}>
											<Button
												color="primary"
												disabled={statusConfirmation === 'LOADING'}
												fullWidth
												onClick={handleClickConfirmReceipt}
												variant="contained">
												{statusConfirmation === 'LOADING'
													? `${t('loading')}...`
													: t('confirmReceipt')}
											</Button>
										</Grid>
									</Grid>
								</Grid>
							)}
							{statusConfirmation === 'EDITING' && (
								<Grid item xs={12}>
									<Grid container justifyContent="flex-end">
										<Grid item>
											<Button
												onClick={() => {
													setStatusConfirmation('DEFAULT');
												}}
												size="small"
												variant="text">
												{t('cancel')}
											</Button>
										</Grid>
									</Grid>
								</Grid>
							)}
						</Grid>
					</Box>
				</Grid>
			);
		}
	}

	if (contentReceipt) {
		contentReceiptWrapper = (
			<Grid item xs={12}>
				<Grid container spacing={2}>
					{contentReceipt}
				</Grid>
			</Grid>
		);
	}

	return (
		<Paper sx={{ overflow: 'hidden' }}>
			<Box
				sx={(theme) => {
					return {
						padding: theme.spacing(2),
					};
				}}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Grid container justifyContent="space-between" spacing={2}>
							<Grid item flex={1} xs={true}>
								<Grid container>
									<Grid item xs={12}>
										<Grid alignItems="center" container spacing={1}>
											<Grid item>
												<Chip
													color="primary"
													label={translateTransactionType(transaction.type, t)}
													size="small"
													variant="outlined"
												/>
											</Grid>
											{transaction.expenseType && (
												<Grid item>
													<Chip
														color="secondary"
														label={(transaction.expenseType as ExpenseType).name}
														size="small"
														variant="outlined"
													/>
												</Grid>
											)}
											<Grid item xs={12}>
												<Typography component="div" variant="h1" fontWeight={700}>
													{renderReceived()}
												</Typography>
												<Typography color="brand.grey.500" fontWeight={600} variant="caption">
													{formatDate({ date: new Date(transaction.date) })}
												</Typography>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs="auto">
								<Grid alignItems="center" container spacing={1}>
									{transaction.traceability && (
										<Grid item xs="auto">
											<ChipTraceability traceability={transaction.traceability} />
										</Grid>
									)}
									{transaction.expenseType && (
										<Grid item xs="auto">
											<Button
												aria-label={t('upload')}
												color="primary"
												fullWidth={true}
												onClick={onClick}>
												<IconCircle color="primary" scheme="dark" type="upload" />
											</Button>
										</Grid>
									)}
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12}>
						<IconList list={list} />
					</Grid>
					{contentReceiptWrapper}
				</Grid>
			</Box>

			<Button
				onClick={handleClickToggle}
				variant="text"
				sx={(theme) => {
					return {
						backgroundColor: theme.palette.brand.blue[500],
						borderRadius: 0,
						padding: theme.spacing(1.5),
						width: '100%',
						color: theme.palette.common.white,
						'&:hover': {
							backgroundColor: theme.palette.brand.blue[500],
						},
					};
				}}>
				<span>{btnExpandLabel}</span>
			</Button>

			{isExpanded && (
				<Box
					sx={(theme) => {
						return {
							backgroundColor: theme.palette.brand.grey[100],
						};
					}}>
					{contentExpanded}
				</Box>
			)}
		</Paper>
	);
};
